/**
 * English UI catalogue for namespace 'easing' (W2-05 easing-templates-raw): the easing picker (DESIGN.md §4.12),
 * the Bezier editor, the curve-part (slice) editor, the value plot, the curve commands, settings rows and the help
 * topic. Family / strength / shape words come from the core catalogue (namespace 'ease').
 */

import { catalogOf, defineMessages } from '../../i18n/define';

const PROP =
  '{prop, select, moveX {Position X} moveY {Position Y} rotate {Rotation} alpha {Opacity} speed {Scroll speed} scaleX {Length / Size X} scaleY {Thickness / Size Y} color {Colour} text {Text} incline {Note tilt} paint {Brush} gif {GIF progress} none {Value} other {{prop}}}';

export const m = defineMessages('easing', {
  // Picker
  title: 'How should it move?',
  searchLabel: 'Search curves',
  searchPlaceholder: 'out quad, oq, 4, bouncy...',
  gridLabel: 'Curves',
  topRowLabel: 'Basic curves',
  rowLabel: '{strength} ({family})',
  cellName: '{strength}, {shape}',
  cellNameFull: '{strength}, {shape}: {rpeName} ({id})',
  cellApprox: '{strength}, {shape}: custom curve fitted to {name}, {error} % off',
  linear: 'Linear',
  hold: 'Stay, then jump',
  holdShort: 'Stay',
  approxBadge: 'Fitted',
  approxTip: "RPE has no number for {name}: this is a custom curve fitted to Phira's, at most {error} % off.",
  differsTip: 'Phira draws this curve differently from RPE.',
  overshootTip: 'This curve goes past its start and end values.',
  overshootWarning: 'Phira shows extra or missing characters when text overshoots (warning PH-16).',
  bakedBadge: 'Baked on export',
  bakedTip:
    'Phira plays scroll speed as straight ramps in this speed mode: eased speed is turned into straight pieces when you export.',
  favouriteBadge: 'Favourite',
  noResults: 'No curve matches "{query}".',
  resultsLabel: 'Matching curves',
  tabCurves: 'Curves',
  tabBezier: 'Custom curve',
  tabsLabel: 'Kind of curve',
  bezierHidden: 'Custom curves are not available here: {reason}',
  bakeOffer: 'Bake into straight pieces...',
  bakeHint:
    'Effect and video values cannot use a custom curve. Baking writes the motion as many short straight pieces.',
  speedGuidedHint: 'Phira plays speed changes as straight ramps. Choose Linear or Stay, or switch to the Full level.',
  revealHint: 'Typing and erasing text cannot use curves that overshoot: Phira would crash.',
  controlHint: "Note controls use RPE's numbered curves only.",
  preview: 'Preview',
  previewOf: `${PROP}: {from} to {to} over {beats}`,
  previewNoSegment: 'The shape of the curve from start to end.',
  previewLive: 'The preview plays the segment with this curve; nothing changes until you pick it.',
  plotLabel: 'Value over time: {name}',
  plotCurrent: 'Current curve',
  recents: 'Recent',
  favourites: 'Favourites',
  noFavourites: 'Press F on a curve to keep it here.',
  addFavourite: 'Add to favourites (F)',
  removeFavourite: 'Remove from favourites (F)',
  appliesTo: 'Applies to {count, plural, one {# segment} other {# segments}}',
  skipped: '{skipped} of {count} segments cannot use this curve: {reason}',
  noneApplies: 'None of the selected segments can use this curve: {reason}',
  keysHint:
    'Arrows move · Enter picks · type an RPE number · / search · B custom curve · Tab part of the curve · F favourite · Esc cancels',
  keysHintGuided: 'Arrows move · Enter picks · type an RPE number · / search · F favourite · Esc cancels',
  apply: 'Use this curve',
  cancel: 'Cancel',
  current: 'Current',
  ids: 'RPE numbers',
  // Slice
  sliceTitle: 'Use part of the curve',
  sliceStart: 'Start of the part',
  sliceEnd: 'End of the part',
  sliceWhole: 'Whole curve',
  sliceReset: 'Use the whole curve',
  sliceLinear: 'Linear has no shape to cut: Phira ignores the part.',
  sliceNotTween: 'Only numbered curves can use part of the curve.',
  sliceControl: 'Note controls always use the whole curve.',
  sliceEmpty: 'The start of the part must come before its end.',
  sliceDegenerate: 'This part starts and ends at the same height: Phira cannot stretch it and would show nothing.',
  sliceRange: '{left} to {right}',
  // Bezier editor
  bezierTitle: 'Custom curve (Bezier)',
  bezierCanvas: 'Curve editor. Drag the two handles, or focus a handle and use the arrow keys (Shift: larger steps).',
  handleStart: 'Start handle',
  handleEnd: 'End handle',
  handleValue: 'x {x}, y {y}',
  x1: 'Start handle X',
  y1: 'Start handle Y',
  x2: 'End handle X',
  y2: 'End handle Y',
  pasteLabel: 'Paste a cubic-bezier',
  pastePlaceholder: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
  pasteApply: 'Use',
  pasteInvalid: 'Write four numbers, like cubic-bezier(0.25, 0.1, 0.25, 1).',
  pasteXRange: 'Handle X values must be between 0 and 1: they were clamped.',
  pasted: 'Pasted {value}.',
  copyCss: 'Copy as cubic-bezier',
  copiedCss: 'Copied {value}.',
  snap: 'Snap to 0.01',
  presets: 'Presets',
  presetsLabel: 'Preset curves',
  'preset.ease': 'Ease',
  'preset.easeIn': 'Ease in',
  'preset.easeOut': 'Ease out',
  'preset.easeInOut': 'Ease in and out',
  'preset.quintInOut': 'Stronger, slow both (fitted)',
  'preset.expoInOut': 'Extreme, slow both (fitted)',
  overshootNote: 'The curve goes past its start or end value.',
  collisionTitle: 'Phira shares this curve with {count, plural, one {another curve} other {# other curves}}',
  collisionOthers:
    'Phira rounds the handles to 0.01 and keeps one curve per rounded set. {count, plural, one {A curve} other {Curves}} on {lines} round the same way.',
  collisionDrawnWith: 'Phira will draw this curve with the handles {points} of the curve that comes first.',
  collisionOwnFirst: 'This curve comes first: Phira will draw the others with these handles.',
  collisionFix: 'Move a handle by 0.01, or use exactly the same handles.',
  bezierApply: 'Use this curve',
  notice: '{text}',
  // Commands
  cmdCopy: 'Copy curve',
  cmdPaste: 'Paste curve',
  cmdBezier: 'Custom curve (Bezier)...',
  cmdPick: 'Change curve...',
  cmdSet: 'Ease: {name}',
  cmdClearRecents: 'Clear recent curves',
  cmdTemplates: 'Motion templates...',
  copiedCurve: 'Copied the curve: {name}.',
  pastedCurve: 'Pasted the curve on {count, plural, one {# segment} other {# segments}}.',
  setCurve: 'Curve set on {count, plural, one {# segment} other {# segments}}.',
  noCurveCopied: 'Copy a curve first.',
  noSegments: 'Select one or more segments first.',
  noProject: 'Open a project first.',
  noActiveLine: 'Choose a line first.',
  recentsCleared: 'Recent curves cleared.',
  txSetCurve: 'Set curve: {name}',
  txPasteCurve: 'Paste curve',
  txBezier: 'Custom curve',
  // Palette titles of the per-curve commands (easing.set.*)
  'set.1': 'Ease: Linear (1)',
  'set.hold': 'Ease: Stay, then jump',
  'set.2': 'Ease: Gentle, slow end (OutSine, 2)',
  'set.3': 'Ease: Gentle, slow start (InSine, 3)',
  'set.4': 'Ease: Soft, slow end (OutQuad, 4)',
  'set.5': 'Ease: Soft, slow start (InQuad, 5)',
  'set.6': 'Ease: Gentle, slow both (InOutSine, 6)',
  'set.7': 'Ease: Soft, slow both (InOutQuad, 7)',
  'set.8': 'Ease: Medium, slow end (OutCubic, 8)',
  'set.9': 'Ease: Medium, slow start (InCubic, 9)',
  'set.10': 'Ease: Strong, slow end (OutQuart, 10)',
  'set.11': 'Ease: Strong, slow start (InQuart, 11)',
  'set.12': 'Ease: Medium, slow both (InOutCubic, 12)',
  'set.13': 'Ease: Strong, slow both (InOutQuart, 13)',
  'set.14': 'Ease: Stronger, slow end (OutQuint, 14)',
  'set.15': 'Ease: Stronger, slow start (InQuint, 15)',
  'set.16': 'Ease: Extreme, slow end (OutExpo, 16)',
  'set.17': 'Ease: Extreme, slow start (InExpo, 17)',
  'set.18': 'Ease: Sharp, slow end (OutCirc, 18)',
  'set.19': 'Ease: Sharp, slow start (InCirc, 19)',
  'set.20': 'Ease: Overshoot, slow end (OutBack, 20)',
  'set.21': 'Ease: Overshoot, slow start (InBack, 21)',
  'set.22': 'Ease: Sharp, slow both (InOutCirc, 22)',
  'set.23': 'Ease: Overshoot, slow both (InOutBack, 23)',
  'set.24': 'Ease: Springy, slow end (OutElastic, 24)',
  'set.25': 'Ease: Springy, slow start (InElastic, 25)',
  'set.26': 'Ease: Bouncy, slow end (OutBounce, 26)',
  'set.27': 'Ease: Bouncy, slow start (InBounce, 27)',
  'set.28': 'Ease: Bouncy, slow both (InOutBounce, 28)',
  'set.29': 'Ease: Springy, slow both (InOutElastic, 29)',
  'set.quintInOut': 'Ease: Stronger, slow both (fitted custom curve)',
  'set.expoInOut': 'Ease: Extreme, slow both (fitted custom curve)',
  // Settings
  settingFavourites: 'Easing favourites and recent curves',
  settingFavouritesHelp: 'The curves kept in the easing picker. Clear them to start over.',
  clearFavourites: 'Clear favourites',
  clearRecents: 'Clear recents',
  favouritesCount:
    '{favourites, plural, =0 {No favourites} one {# favourite} other {# favourites}}, {recents, plural, =0 {no recent curves} one {# recent curve} other {# recent curves}}',
  settingShowIds: 'Easing picker: always show RPE numbers',
  settingShowIdsHelp: 'Show the RPE easing numbers on every curve, also at the Guided level.',
  // Help
  helpTitle: 'Choosing how things move',
  helpBody:
    'Each segment between two keyframes has a curve. Rows are strengths (Gentle to Bouncy) and columns say where the motion is slow: at the end, at the start or both. Hover a curve to preview it on the real line; press Enter or click to use it. Type an RPE number (two digits within a moment), press / to search ("out quad", "oq", "4") and F to keep a favourite. The Full level also shows the RPE numbers, lets you use only part of a curve and draw a custom Bezier curve (press B, move the handles with the arrow keys, Enter to use it, B to go back to the grid).',
  helpTemplatesTitle: 'Motion templates',
  helpTemplatesBody:
    'Templates write ordinary keyframes for common motions: fade in and out, slide in and out, move to a point, spin, swing, pulse, shake, flash a colour and orbit the parent line. The preview loops the span while you adjust it; Apply makes it one undoable step. At the Full level a template can go on a new layer so the existing keyframes stay untouched.',
});

export default catalogOf(m);
